import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface NoPriceDialogData {
  productName?: string;
  productId?: number;
  is_ht: boolean;
  // Conditionnement scanné : le prix demandé est TOUJOURS celui d'une unité de base (UVC), addLine multiplie par qtybaseuom.
  packagingName?: string; // « Carton de 4 Paquets » (getPackagingName)
  packagingUnitName?: string; // « Carton »
  baseUnitName?: string; // « Paquet »
  qtybaseuom?: number; // 4
  freePrice?: boolean; // Article 1 (DIVERS) : tout montant accepté, 0 et négatif compris.
}

export interface NoPriceDialogResult {
  price: number;
  is_ht: boolean;
}

@Component({
  selector: 'app-no-price-dialog',
  standalone: true,
  styleUrls: ['./noPriceDialog.component.scss'],
  imports: [DecimalPipe, MatDialogModule, MatDialogContent, MatDialogActions, MatButtonModule, MatIconModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './noPriceDialog.component.html'
})
export class NoPriceDialog {
  priceInput = new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]);

  constructor(
    public dialogRef: MatDialogRef<NoPriceDialog>,
    @Inject(MAT_DIALOG_DATA) public data: NoPriceDialogData
  ) {
    if (data.freePrice) this.priceInput.setValidators(Validators.required);
  }

  // « par » plutôt qu'un article : le genre de l'unité (« un boîte ») n'est pas connu.
  get priceLabel(): string {
    const tax = this.data.is_ht ? 'HT' : 'TTC';
    return this.isMultiple || this.data.baseUnitName ? `Prix ${tax} par ${this.baseUnit} (€)` : `Prix ${tax} (€)`;
  }

  // Noms par défaut : une unité de base ou un conditionnement sans nom (modèle neuf, c_values pas encore chargé) garde le rappel.
  get baseUnit(): string {
    return (this.data.baseUnitName ?? '').toLowerCase() || 'unité de base';
  }

  get packagingUnit(): string {
    return (this.data.packagingUnitName ?? '').toLowerCase() || 'conditionnement';
  }

  // « paquets », « unités de base », « colis » : seul le premier mot prend la marque du pluriel.
  get baseUnitPlural(): string {
    const [first, ...rest] = this.baseUnit.split(' ');
    return [/[sxz]$/.test(first) ? first : first + 's', ...rest].join(' ');
  }

  // Vrai dès que le conditionnement groupe plusieurs unités de base, noms connus ou non : addLine multiplie le prix saisi par le colisage.
  get isMultiple(): boolean {
    return (this.data.qtybaseuom ?? 1) > 1;
  }

  get packagingTotal(): number | null {
    const price = this.priceInput.value;
    if (!this.isMultiple || !price || price <= 0) return null;
    return Math.round(price * (this.data.qtybaseuom ?? 1) * 100) / 100;
  }

  onConfirm(): void {
    if (this.priceInput.invalid || this.priceInput.value === null) return;
    this.dialogRef.close({ price: this.priceInput.value, is_ht: this.data.is_ht } as NoPriceDialogResult);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
